import {
  focusEditor,
  getAboveNode,
  getEndPoint,
  getPluginOptions,
  getStartPoint,
  isType,
  someNode,
  useEditorRef,
  useHotkeys,
  usePlateSelectors,
} from '@udecode/plate-common';
import {
  getDefaultBoundingClientRect,
  getRangeBoundingClientRect,
  UseVirtualFloatingOptions,
} from '@udecode/plate-floating';
import { LinkPlugin } from '@udecode/plate-link';
import { useCallback, useEffect } from 'react';

import {
  ELEMENT_ANCHOR_LINK,
  ELEMENT_INTERLINK,
  ELEMENT_LINK,
} from '../../../plateTypes';
import { unwrapLink } from '../transforms';
import { triggerFloatingLinkEdit } from '../utils/triggerFloatingLinkEdit';
import {
  floatingLinkActions,
  floatingLinkSelectors,
  useFloatingLinkSelectors,
} from './floatingLinksStore';
import { useFloatingInterlinkEscape } from './useFloatingLinkEscape';
import { useVirtualFloatingLink } from './useVirtualFloatingLink';

export const useFloatingLinkEditState = ({
  floatingOptions,
}: {
  floatingOptions?: UseVirtualFloatingOptions;
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
} = {}) => {
  const editor = useEditorRef();
  const keyEditor = usePlateSelectors().versionEditor();
  const mode = useFloatingLinkSelectors().mode();
  const interlinkOpen = useFloatingLinkSelectors().isOpen(editor.id);

  // Keep link plugin hotkeys working
  const { triggerFloatingLinkHotkeys } = getPluginOptions<LinkPlugin>(
    editor,
    ELEMENT_LINK,
  );

  const getBoundingClientRect = useCallback(() => {
    const entry = getAboveNode(editor, {
      match: (node) =>
        isType(editor, node, [
          ELEMENT_LINK,
          ELEMENT_ANCHOR_LINK,
          ELEMENT_INTERLINK,
        ]),
    });

    if (entry) {
      const [, path] = entry;
      return getRangeBoundingClientRect(editor, {
        anchor: getStartPoint(editor, path),
        focus: getEndPoint(editor, path),
      });
    }

    return getDefaultBoundingClientRect();
  }, [editor]);

  const isOpen = interlinkOpen && mode === 'edit';

  const floating = useVirtualFloatingLink({
    editorId: editor.id,
    open: isOpen,
    getBoundingClientRect,
    ...floatingOptions,
  });
  const { update } = floating;

  useEffect(() => {
    if (
      editor.selection &&
      someNode(editor, {
        match: { type: ELEMENT_INTERLINK },
      })
    ) {
      floatingLinkActions.show('edit', editor.id);
      floatingLinkActions.linkType('article');
      update();
      return;
    }

    if (
      editor.selection &&
      someNode(editor, {
        match: { type: ELEMENT_LINK },
      })
    ) {
      floatingLinkActions.show('edit', editor.id);
      floatingLinkActions.linkType('url');
      update();
      return;
    }

    if (
      editor.selection &&
      someNode(editor, {
        match: { type: ELEMENT_ANCHOR_LINK },
      })
    ) {
      floatingLinkActions.show('edit', editor.id);
      floatingLinkActions.linkType('anchor');
      update();
      return;
    }

    if (floatingLinkSelectors.mode() === 'edit') {
      floatingLinkActions.hide();
    }
  }, [editor, keyEditor, update]);

  useHotkeys(
    triggerFloatingLinkHotkeys!,
    (e) => {
      if (
        floatingLinkSelectors.mode() === 'edit' &&
        triggerFloatingLinkEdit(editor)
      ) {
        e.preventDefault();
      }
    },
    {
      enableOnContentEditable: true,
    },
    [],
  );

  useFloatingInterlinkEscape();

  return {
    floating,
  };
};

export const useFloatingLinkEdit = (
  state: ReturnType<typeof useFloatingLinkEditState>,
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
) => {
  const editor = useEditorRef();

  return {
    ref: state.floating.refs.setFloating,
    props: {
      style: {
        ...state.floating.style,
        zIndex: 1,
      },
    },
    editButtonProps: {
      onClick: useCallback(() => {
        triggerFloatingLinkEdit(editor);
      }, [editor]),
    },
    unlinkButtonProps: {
      onClick: useCallback(() => {
        unwrapLink(editor);
        focusEditor(editor, editor.selection!);
      }, [editor]),
    },
  };
};
